import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type SyntheticEvent,
} from 'react';
import ReactCrop, {
  convertToPixelCrop,
  type Crop,
  type PixelCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { buildInitialCrop, cropImageToFile } from '../lib/cropImage';
import {
  isRasterImageFile,
  prepareImageForUpload,
  PrepareImageError,
} from '../lib/prepareImageForUpload';
import { useSnackbar } from 'notistack';

export type ImageCropDialogProps = {
  open: boolean;
  sourceFile: File | null;
  aspect?: number;
  title?: string;
  onClose: () => void;
  onConfirm: (file: File) => void | Promise<void>;
};

const ImageCropDialog = ({
  open,
  sourceFile,
  aspect = 1,
  title = 'Kép metszése',
  onClose,
  onConfirm,
}: ImageCropDialogProps) => {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isApplying, setIsApplying] = useState(false);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !sourceFile) {
      setPreviewUrl(null);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(sourceFile);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [open, sourceFile]);

  useEffect(() => {
    if (!open) {
      setCrop(undefined);
      setCompletedCrop(undefined);
      setIsApplying(false);
    }
  }, [open]);

  const handleImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = event.currentTarget;
      const initial = buildInitialCrop(width, height, aspect);
      setCrop(initial);
      setCompletedCrop(convertToPixelCrop(initial, width, height));
    },
    [aspect]
  );

  const handleConfirm = useCallback(async () => {
    if (
      !sourceFile ||
      !imageRef.current ||
      !completedCrop?.width ||
      !completedCrop.height
    ) {
      return;
    }

    setIsApplying(true);
    try {
      const cropped = await cropImageToFile(
        imageRef.current,
        completedCrop,
        sourceFile
      );
      // Crop UI already ran — compress/resize without re-cropping.
      const prepared = isRasterImageFile(cropped)
        ? await prepareImageForUpload(cropped, { fileName: cropped.name })
        : cropped;
      await onConfirm(prepared);
      onClose();
    } catch (error) {
      const message =
        error instanceof PrepareImageError
          ? 'A kép nem készíthető elő feltöltésre. Próbálj kisebb fájlt vagy más formátumot.'
          : 'A kép metszése sikertelen.';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsApplying(false);
    }
  }, [completedCrop, enqueueSnackbar, onClose, onConfirm, sourceFile]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            minHeight: 280,
            maxHeight: '70vh',
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            borderRadius: 1,
            p: 1,
          }}
        >
          {previewUrl ? (
            <ReactCrop
              crop={crop}
              onChange={(next) => setCrop(next)}
              onComplete={(next) => setCompletedCrop(next)}
              aspect={aspect}
              style={{ maxHeight: '65vh' }}
            >
              <img
                ref={imageRef}
                src={previewUrl}
                alt="Metszendő kép"
                style={{
                  maxHeight: '65vh',
                  width: '100%',
                  objectFit: 'contain',
                }}
                onLoad={handleImageLoad}
              />
            </ReactCrop>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isApplying}>
          Mégse
        </Button>
        <Button
          variant="contained"
          disabled={
            isApplying || !completedCrop?.width || !completedCrop?.height
          }
          onClick={() => void handleConfirm()}
          startIcon={
            isApplying ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
        >
          {isApplying ? 'Előkészítés…' : 'Alkalmazás'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropDialog;
